const Sentry = require('@sentry/browser');

const SPREEDLY_INIT_TIMEOUT_MS = 10000;

window.iq.SPREEDLY = null;
window.iq.SPREEDLY_OPTIONS = null;

let spreedlyDialogTrigger = null;
let spreedlyInitAbortController = null;
let spreedlyAttempt = 0;
let spreedlyDismissalLocked = false;

const getSpreedlyDialog = () => document.getElementById('spreedly_checkout_dialog');

const blockSpreedlyEscape = (event) => {
	if (spreedlyDismissalLocked && (event.key === 'Escape' || event.key === 'Esc')) {
		event.preventDefault();
		event.stopImmediatePropagation();
	}
};

const blockSpreedlyCancel = (event) => {
	if (spreedlyDismissalLocked) {
		event.preventDefault();
		event.stopImmediatePropagation();
	}
};

const setSpreedlyDismissalLocked = (locked) => {
	spreedlyDismissalLocked = locked;
	const dialog = getSpreedlyDialog();
	const closeButton = dialog?.querySelector('.spreedly_checkout_close');
	if (closeButton) {
		closeButton.disabled = locked;
	}
	if (dialog) {
		dialog.setAttribute('closedby', locked ? 'none' : 'closerequest');
	}
	if (locked) {
		window.addEventListener('keydown', blockSpreedlyEscape, true);
		dialog?.addEventListener('cancel', blockSpreedlyCancel, true);
	} else {
		window.removeEventListener('keydown', blockSpreedlyEscape, true);
		dialog?.removeEventListener('cancel', blockSpreedlyCancel, true);
	}
};

const reEnablePaymentSubmit = () => {
	if (window.jQuery('#paymentToken').val() !== '') {
		return;
	}
	window
		.jQuery('#paymentToken')
		.closest('form')
		.find('button[type=submit]')
		.prop('disabled', false)
		.removeClass('disabled');
};

const closeSpreedlyDialog = () => {
	const dialog = getSpreedlyDialog();
	if (dialog && dialog.open) {
		dialog.close();
	}
	document.documentElement.classList.remove('spreedly_checkout_open');
	setSpreedlyDismissalLocked(false);
	reEnablePaymentSubmit();
	if (spreedlyDialogTrigger && spreedlyDialogTrigger.isConnected) {
		spreedlyDialogTrigger.focus();
	}
	spreedlyDialogTrigger = null;
};

const requestSpreedlyDialogClose = () => {
	if (spreedlyDismissalLocked) {
		return;
	}
	spreedlyAttempt += 1;
	spreedlyInitAbortController?.abort();
	try {
		window.iq.SPREEDLY?.close();
	} finally {
		closeSpreedlyDialog();
	}
};

const openSpreedlyDialog = () => {
	const dialog = getSpreedlyDialog();
	if (!dialog.open) {
		spreedlyDialogTrigger = document.activeElement;
	}
	dialog.setAttribute('aria-busy', 'true');
	document.documentElement.classList.add('spreedly_checkout_open');
	if (!dialog.open) {
		dialog.showModal();
	}
	dialog.querySelector('.spreedly_checkout_close').focus();
};

const resetSpreedlyDialogForAttempt = () => {
	setSpreedlyDismissalLocked(false);
	window.jQuery('#spreedly_checkout_mount').empty().removeAttr('hidden');
	window.jQuery('#spreedly_lock_icon').removeAttr('hidden');
	window.jQuery('#spreedly_checkout_loading').show();
	window.jQuery('#spreedly_checkout_processing').attr('hidden', true);
	window.jQuery('#spreedly_checkout_error').text('').attr('hidden', true);
	window.jQuery('#spreedly_checkout_retry').attr('hidden', true);
	window.jQuery('#spreedly_checkout_container').scrollTop(0);
	getSpreedlyDialog().setAttribute('aria-busy', 'true');
};

const showRetryableSpreedlyError = () => {
	setSpreedlyDismissalLocked(false);
	window.jQuery('#spreedly_checkout_loading').hide();
	window.jQuery('#spreedly_checkout_processing').attr('hidden', true);
	window.jQuery('#spreedly_lock_icon').removeAttr('hidden');
	window.jQuery('#spreedly_checkout_mount').empty().removeAttr('hidden');
	window
		.jQuery('#spreedly_checkout_error')
		.html('Unable to load secure checkout.<br/>Please try again.')
		.removeAttr('hidden');
	window.jQuery('#spreedly_checkout_retry').removeAttr('hidden').trigger('focus');
	getSpreedlyDialog().setAttribute('aria-busy', 'false');
	reEnablePaymentSubmit();
};

const showSpreedlyProcessing = () => {
	setSpreedlyDismissalLocked(true);
	window.jQuery('#spreedly_checkout_loading').hide();
	window.jQuery('#spreedly_lock_icon').attr('hidden', true);
	window.jQuery('#spreedly_checkout_mount').attr('hidden', true);
	window.jQuery('#spreedly_checkout_processing').removeAttr('hidden').attr('tabindex', '-1').trigger('focus');
	window.jQuery('#spreedly_checkout_container').scrollTop(0);
};

const handleSpreedlyError = (error) => {
	const normalizedError = error instanceof Error ? error : new Error(String(error));
	console.error('Spreedly checkout error:', normalizedError);
	showRetryableSpreedlyError();
	try {
		Sentry.captureException(normalizedError, {
			tags: { component: 'spreedly-checkout' }
		});
	} catch (captureError) {
		console.error('Unable to report Spreedly checkout error:', captureError);
	}
};

module.exports = {
	init: (options) => {
		window.iq.SPREEDLY_OPTIONS = Object.assign({}, options);
		const dialog = getSpreedlyDialog();
		window.jQuery(dialog).on('click', function (event) {
			if (event.target === this) {
				requestSpreedlyDialogClose();
			}
		});
		window.jQuery(dialog).on('cancel', function (event) {
			event.preventDefault();
			requestSpreedlyDialogClose();
		});
		window.jQuery(dialog).find('.spreedly_checkout_close').on('click', requestSpreedlyDialogClose);
		window.jQuery('#spreedly_checkout_retry').on('click', () => module.exports.tokenize());
	},
	update: (params) => {
		if (params.display) {
			window.iq.SPREEDLY_OPTIONS = Object.assign({}, window.iq.SPREEDLY_OPTIONS, {
				display: Object.assign({}, window.iq.SPREEDLY_OPTIONS.display, params.display)
			});
		}
	},
	tokenize: async () => {
		const attempt = ++spreedlyAttempt;
		spreedlyInitAbortController?.abort();
		const abortController = new AbortController();
		spreedlyInitAbortController = abortController;
		const timeout = setTimeout(() => abortController.abort(), SPREEDLY_INIT_TIMEOUT_MS);

		resetSpreedlyDialogForAttempt();
		openSpreedlyDialog();

		try {
			const sdkInitParamsResponse = await fetch('/p/api/spreedly/init', {
				signal: abortController.signal
			});
			if (!sdkInitParamsResponse.ok) {
				throw new Error(`Unable to initialize Spreedly: HTTP ${sdkInitParamsResponse.status}`);
			}
			const params = await sdkInitParamsResponse.json();

			const sdk = new window.SpreedlyExpressCheckout(params);
			window.iq.SPREEDLY = sdk;

			sdk.on('ready', () => {
				if (attempt !== spreedlyAttempt) {
					return;
				}
				window.jQuery('#spreedly_checkout_container').scrollTop(0);
				window.jQuery('#spreedly_checkout_loading').hide();
				getSpreedlyDialog().setAttribute('aria-busy', 'false');
			});
			sdk.on('tokenGenerated', (response) => {
				if (attempt !== spreedlyAttempt) {
					return;
				}
				try {
					showSpreedlyProcessing();
					if (window.iq.SPREEDLY_OPTIONS.onSubmitting) {
						window.iq.SPREEDLY_OPTIONS.onSubmitting();
					}
					const token = response.tokenResponse.payment_method.token;
					window.jQuery('#paymentToken').val(token).closest('form').submit();
				} catch (error) {
					handleSpreedlyError(error);
				}
			});
			sdk.on('close', () => {
				if (attempt !== spreedlyAttempt || spreedlyDismissalLocked) {
					return;
				}
				spreedlyAttempt += 1;
				closeSpreedlyDialog();
			});
			sdk.on('error', (error) => {
				if (attempt !== spreedlyAttempt) {
					return;
				}
				handleSpreedlyError(error);
			});

			const displayDefaults = {
				title: 'Secure Checkout',
				submitBtnText: undefined,
				footerText: ' ',
				processingText: 'Processing...'
			};
			const displayOptions = Object.assign({}, displayDefaults, window.iq.SPREEDLY_OPTIONS.display ?? {});
			if (!displayOptions.submitBtnText && window.iq.SPREEDLY_OPTIONS.submit_label) {
				displayOptions.submitBtnText = window.iq.SPREEDLY_OPTIONS.submit_label;
			}

			for (const k of ['title', 'submitBtnText', 'footerText', 'processingText']) {
				if (displayOptions[k]) {
					sdk.updateTextElement(k, displayOptions[k]);
				}
			}
			if (!getSpreedlyDialog().open || attempt !== spreedlyAttempt) {
				return;
			}
			sdk.expressCheckout({
				parentContainerId: 'spreedly_checkout_mount'
			});
		} catch (error) {
			if (attempt !== spreedlyAttempt || (!getSpreedlyDialog().open && abortController.signal.aborted)) {
				return;
			}
			handleSpreedlyError(error);
		} finally {
			clearTimeout(timeout);
			if (spreedlyInitAbortController === abortController) {
				spreedlyInitAbortController = null;
			}
		}
	}
};
